import { Document, Page, StyleSheet, Text, View, Image, pdf } from '@react-pdf/renderer'
import { max, min } from 'lodash'
import Chart from 'chart.js/auto'
import { PTInteractiveResult, PTKldB } from '../../shared/model'
import warning from '../../assets/pt-interactive/warning.png'
import info from '../../assets/pt-interactive/info.png'

const styles = StyleSheet.create({
  page: {
    padding: 60,
  },
  bodyText: {
    fontFamily: 'Raleway',
    fontSize: '18px',
    fontWeight: 400,
    lineHeight: 0,
    textAlign: 'justify',
  },
  bodyTextBold: {
    fontFamily: 'RalewayBold',
    fontSize: '18px',
    fontWeight: 400,
    lineHeight: 0,
  },
  bodySuperScript: {
    fontFamily: 'RalewayBold',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 0,
    verticalAlign: 'super',
  },
  bodyNotesText: {
    fontFamily: 'Raleway',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 0,
    textAlign: 'justify',
  },
})

export const downloadInteractive = async (
  options: number[],
  lang: string,
  results: PTInteractiveResult[],
  selectedKldb: PTKldB | null,
  salary: number,
  company: string,
  theme: any,
  t: any,
) => {
  const currentDate = new Date()
  const cwiObj = results.find((r) => r.type === 'cwi')
  const tatObj = results.find((rv) => rv.type === 'Tätigkeitsschlüssel')
  let cwiImage = null
  let tatImage = null

  const canvas = document.createElement('canvas')
  canvas.height = 800
  canvas.width = 1600
  const context = canvas.getContext('2d')

  if (options.includes(3)) {
    const orderedCwiVales = cwiObj?.values.sort((a, b) => a.order - b.order) ?? []
    const cwiGraphData = {
      labels: orderedCwiVales.map((orv) => t(`pt.results.label.${orv.id}`, { lng: lang })),
      datasets: [
        {
          data: orderedCwiVales.map((orv) => orv.salary),
          backgroundColor: theme.palette.primary.main,
          pointRadius: 18,
        },
      ],
    }
    if (context) {
      const cwiGraphOptions: any = {
        responsive: false,
        maintainAspectRatio: false,
        devicePixelRatio: 1.5,
        layout: {
          padding: {
            top: 50,
            right: 270,
          },
        },
        animation: {
          duration: 0,
        },
        plugins: {
          // @ts-ignore
          legendMargin: false,
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
            mode: 'dataset',
          },
          datalabels: {
            anchor: 'end',
            align: 'end',
            color: theme.palette.text.secondary,
            font: {
              size: 28,
            },
            formatter: (value: any) =>
              `${Number(value).toLocaleString(lang, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })} \u20ac`,
          },
          annotation: {
            clip: false,
            annotations: {
              line1: {
                type: 'line',
                value: salary,
                scaleID: 'y',
                borderColor: theme.palette.primary.main,
                borderWidth: 4,
                label: {
                  backgroundColor: 'transparent',
                  color: theme.palette.primary.main,
                  font: {
                    size: 28,
                    weight: 'normal',
                    lineHeight: 1.5,
                  },
                  display: true,
                  content: [
                    t('pt.results.line1', { lng: lang }),
                    t('pt.results.line2', { lng: lang }),
                    `${Number(salary).toLocaleString(lang, {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })} \u20ac`,
                  ],
                  position: 'end',
                  textAlign: 'left',
                  xAdjust: 265,
                  yAdjust: 25,
                },
              },
            },
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            border: {
              color: theme.palette.text.disabled,
            },
            ticks: {
              color: theme.palette.text.primary,
              font: { size: 36 },
            },
            offset: true,
            type: 'category',
            labels: orderedCwiVales.map((orv) => t(`pt.results.label.${orv.id}`, { lng: lang })),
          },
          y: {
            type: 'linear',
            offset: true,
            min:
              (min(orderedCwiVales.map((o) => o.salary)) ?? 0) > salary
                ? salary
                : min(orderedCwiVales.map((o) => o.salary)),
            max:
              (max(orderedCwiVales.map((o) => o.salary)) ?? 0) < salary
                ? salary
                : max(orderedCwiVales.map((o) => o.salary)),
            grid: {
              display: true,
              color: theme.palette.divider,
            },
            border: {
              color: theme.palette.text.disabled,
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 6,
              color: theme.palette.text.primary,
              font: { size: 36 },
              callback: (value: any) =>
                `${Number(value).toLocaleString(lang, {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })} \u20ac`,
            },
          },
        },
      }
      const myChart = new Chart(context, {
        type: 'scatter',
        data: cwiGraphData,
        options: cwiGraphOptions,
      })
      cwiImage = myChart.toBase64Image()
      myChart.destroy()
      context.clearRect(0, 0, canvas.width, canvas.height)
    }
  }

  if (options.includes(5)) {
    const orderedCwiVales = tatObj?.values.sort((a, b) => a.order - b.order) ?? []
    const cwiGraphData = {
      labels: orderedCwiVales.map((orv) => t(`pt.results.label.${orv.id}`, { lng: lang })),
      datasets: [
        {
          data: orderedCwiVales.map((orv) => orv.salary),
          backgroundColor: theme.palette.primary.main,
          pointRadius: 18,
        },
      ],
    }
    if (context) {
      const tatGraphOptions: any = {
        responsive: false,
        maintainAspectRatio: false,
        devicePixelRatio: 1.5,
        layout: {
          padding: {
            top: 50,
            right: 270,
          },
        },
        animation: {
          duration: 0,
        },
        plugins: {
          // @ts-ignore
          legendMargin: false,
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
            mode: 'dataset',
          },
          datalabels: {
            anchor: 'end',
            align: 'end',
            color: theme.palette.text.secondary,
            font: {
              size: 28,
            },
            formatter: (value: any) =>
              `${Number(value).toLocaleString(lang, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })} \u20ac`,
          },
          annotation: {
            clip: false,
            annotations: {
              line1: {
                type: 'line',
                value: salary,
                scaleID: 'y',
                borderColor: theme.palette.primary.main,
                borderWidth: 4,
                label: {
                  backgroundColor: 'transparent',
                  color: theme.palette.primary.main,
                  font: {
                    size: 28,
                    weight: 'normal',
                    lineHeight: 1.5,
                  },
                  display: true,
                  content: [
                    t('pt.results.line1', { lng: lang }),
                    t('pt.results.line2', { lng: lang }),
                    `${Number(salary).toLocaleString(lang, {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })} \u20ac`,
                  ],
                  position: 'end',
                  textAlign: 'left',
                  xAdjust: 270,
                  yAdjust: 40,
                },
              },
            },
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            border: {
              color: theme.palette.text.disabled,
            },
            ticks: {
              color: theme.palette.text.primary,
              font: { size: 36 },
            },
            offset: true,
            type: 'category',
            labels: orderedCwiVales.map((orv) => t(`pt.results.label.${orv.id}`, { lng: lang })),
          },
          y: {
            type: 'linear',
            offset: true,
            min:
              (min(orderedCwiVales.map((o) => o.salary)) ?? 0) > salary
                ? salary
                : min(orderedCwiVales.map((o) => o.salary)),
            max:
              (max(orderedCwiVales.map((o) => o.salary)) ?? 0) < salary
                ? salary
                : max(orderedCwiVales.map((o) => o.salary)),
            grid: {
              display: true,
              color: theme.palette.divider,
            },
            border: {
              color: theme.palette.text.disabled,
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 6,
              color: theme.palette.text.primary,
              font: { size: 36 },
              callback: (value: any) =>
                `${Number(value).toLocaleString(lang, {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })} \u20ac`,
            },
          },
        },
      }
      const myChart = new Chart(context, {
        type: 'scatter',
        data: cwiGraphData,
        options: tatGraphOptions,
      })
      tatImage = myChart.toBase64Image()
      myChart.destroy()
      context.clearRect(0, 0, canvas.width, canvas.height)
    }
  }

  const Template = (
    <Document>
      <Page size="A4" orientation="portrait" dpi={150} style={styles.page}>
        <Text style={{ ...styles.bodyTextBold, fontSize: '24px' }}>
          {t('pt.results.pdf.title', { lng: lang })}
        </Text>
        <Text style={{ ...styles.bodyText, fontSize: '14px' }}>
          {t('pt.results.pdf.desc', { lng: lang })}
          <Text style={styles.bodySuperScript}>1</Text>
        </Text>
        <View style={{ display: 'flex', flexDirection: 'row', marginTop: 32 }}>
          <View style={{ width: '40%', marginRight: 48 }}>
            <Text style={styles.bodyTextBold}>{t('pt.results.pdf.company', { lng: lang })}</Text>
            <Text style={{ ...styles.bodyText, marginTop: '8px' }}>{company}</Text>
          </View>
          <View style={{ width: '30%' }}>
            <Text style={styles.bodyTextBold}>{t('pt.results.pdf.date', { lng: lang })}</Text>
            <Text style={{ ...styles.bodyText, marginTop: '8px' }}>
              {currentDate.toLocaleDateString(lang === 'en' ? 'en-US' : 'de-DE', {
                month: 'long',
                year: 'numeric',
                day: '2-digit',
              })}
            </Text>
          </View>
        </View>
        <View
          style={{
            backgroundColor: '#CECECE',
            width: 500,
            padding: '8px 24px',
            marginTop: 24,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
        >
          <Text style={styles.bodyTextBold}>{t('pt.results.pdf.emp.info', { lng: lang })}</Text>
        </View>
        <View
          style={{
            border: '2px solid #CECECE',
            borderRadius: 4,
            borderTopLeftRadius: 0,
            padding: '16px 24px',
          }}
        >
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <View style={{ width: '40%', marginRight: 48 }}>
              <Text style={styles.bodyText}>{t('pt.results.pdf.full.name', { lng: lang })}</Text>
              <View
                style={{
                  border: '1px solid #DAF0FF',
                  borderRadius: 2,
                  width: '100%',
                  height: 30,
                  marginTop: 8,
                  marginBottom: 24,
                }}
              />
            </View>
            <View style={{ width: '40%' }}>
              <Text style={styles.bodyText}>{t('pt.results.pdf.comp.id', { lng: lang })}</Text>
              <View
                style={{
                  border: '1px solid #DAF0FF',
                  borderRadius: 2,
                  width: '100%',
                  height: 30,
                  marginTop: 8,
                  marginBottom: 24,
                }}
              />
            </View>
          </View>
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <View style={{ width: '40%', marginRight: 48 }}>
              <Text style={styles.bodyText}>
                {t('pt.assistance.selected.occ.main', { lng: lang })}
              </Text>
              <View style={{ width: '100%', marginTop: 8, marginBottom: 24 }}>
                <Text style={styles.bodyTextBold}>
                  {lang === 'de' ? selectedKldb?.titleDe : selectedKldb?.titleEn}
                </Text>
              </View>
            </View>
            <View style={{ width: '40%' }}>
              <Text style={styles.bodyText}>
                {t('pt.results.pdf.working.hours', { lng: lang })}
                <Text style={styles.bodySuperScript}>2</Text>
              </Text>
              <View
                style={{
                  border: '1px solid #DAF0FF',
                  borderRadius: 2,
                  width: '100%',
                  height: 30,
                  marginTop: 8,
                  marginBottom: 24,
                }}
              />
            </View>
          </View>
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <View style={{ width: '40%', marginRight: 48 }}>
              <Text style={styles.bodyText}>
                {t('pt.assistance.selected.occ.salary', { lng: lang })}
                <Text style={styles.bodySuperScript}>3,4</Text>
              </Text>
              <View style={{ width: '100%', height: 30, marginTop: 8 }}>
                <Text style={styles.bodyTextBold}>
                  {`${salary.toLocaleString(lang, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} \u20ac`}
                </Text>
              </View>
            </View>
            <View style={{ width: '40%' }}>
              <Text style={styles.bodyText}>{t('pt.results.pdf.annual.gross', { lng: lang })}</Text>
              <View
                style={{
                  border: '1px solid #DAF0FF',
                  borderRadius: 2,
                  width: '100%',
                  height: 30,
                  marginTop: 8,
                }}
              />
            </View>
          </View>
        </View>
        <View
          style={{
            backgroundColor: '#CECECE',
            width: 300,
            padding: '8px 24px',
            marginTop: 24,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
        >
          <Text style={styles.bodyTextBold}>Pay Transparency</Text>
        </View>
        <View
          style={{
            border: '2px solid #CECECE',
            borderRadius: 4,
            borderTopLeftRadius: 0,
            padding: '16px 24px',
          }}
        >
          <Text style={{ ...styles.bodyText, fontSize: '14px' }}>
            {t('pt.results.desc.1', { lng: lang })}
            <Text style={styles.bodySuperScript}>5</Text> {t('pt.results.desc.2', { lng: lang })}
          </Text>
          {(options.includes(2) || options.includes(3)) && (
            <View
              style={{
                backgroundColor: '#F5F5F5',
                borderRadius: 2,
                marginTop: 16,
                marginBottom: 8,
                padding: 8,
              }}
              wrap={false}
            >
              <Text style={styles.bodyText}>{t('pt.results.within', { lng: lang })}</Text>
              <Text style={{ ...styles.bodyTextBold, marginTop: 4 }}>
                {t('pt.results.cwi.title', { lng: lang })}
                <Text style={styles.bodySuperScript}>6</Text> {cwiObj?.id}
              </Text>
            </View>
          )}
          {options.includes(2) && (
            <View
              style={{ display: 'flex', flexDirection: 'row', margin: '8px 16px' }}
              wrap={false}
            >
              {cwiObj?.values
                .sort((a, b) => a.order - b.order)
                .map((ov) => {
                  const isGreater =
                    parseFloat((ov.salary ?? 0).toFixed(2)) > parseFloat(salary.toFixed(2))
                  return (
                    <View style={{ width: '30%', marginRight: 50 }}>
                      <Text style={styles.bodyText}>
                        {t(`pt.results.label.${ov.id}`, { lng: lang })}{' '}
                        <Text style={{ ...styles.bodyText, fontSize: '14px' }}>
                          ({ov.employees} {t('pt.results.label.employees', { lng: lang })})
                        </Text>
                      </Text>
                      {ov.salary !== null ? (
                        <View
                          style={{
                            backgroundColor: isGreater ? '#FFD2D3' : '#F5F5F5',
                            paddingVertical: 8,
                            paddingHorizontal: 16,
                            marginTop: 8,
                            borderRadius: 4,
                          }}
                        >
                          <Text style={styles.bodyTextBold}>
                            {`${ov.salary.toLocaleString(lang, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })} \u20ac`}
                          </Text>
                        </View>
                      ) : null}
                      {ov.salary !== null &&
                        parseFloat(ov.salary.toFixed(2)) !== parseFloat(salary.toFixed(2)) && (
                          <View style={{ display: 'flex', flexDirection: 'row', marginTop: 8 }}>
                            <Image
                              src={isGreater ? warning : info}
                              style={{
                                height: '12px',
                                width: '14px',
                                objectFit: 'contain',
                                marginRight: '8px',
                                marginTop: 2.5,
                              }}
                            />
                            <Text style={{ ...styles.bodyText, fontSize: '14px' }}>
                              {isGreater
                                ? t('pt.results.below', { lng: lang })
                                : t('pt.results.above', { lng: lang })}
                            </Text>
                          </View>
                        )}
                    </View>
                  )
                })}
            </View>
          )}
          {cwiImage && (
            <View style={{ width: '100%', height: 450, marginTop: '48px' }} wrap={false}>
              <Text style={styles.bodyTextBold}>{t('pt.results.graph.title', { lng: lang })}</Text>
              <Image src={cwiImage} style={{ width: '100%', height: 350, objectFit: 'contain' }} />
            </View>
          )}
          {(options.includes(4) || options.includes(5)) && (
            <View
              style={{
                backgroundColor: '#F5F5F5',
                borderRadius: 2,
                marginTop: 16,
                marginBottom: 8,
                padding: 8,
              }}
              wrap={false}
            >
              <Text style={styles.bodyText}>{t('pt.results.within', { lng: lang })}</Text>
              <Text style={{ ...styles.bodyTextBold, marginTop: 4 }}>
                {t('pt.results.tat.title', { lng: lang })}
                <Text style={styles.bodySuperScript}>7</Text> {tatObj?.id}
              </Text>
            </View>
          )}
          {options.includes(4) && (
            <View
              style={{ display: 'flex', flexDirection: 'row', margin: '8px 16px' }}
              wrap={false}
            >
              {tatObj?.values
                .sort((a, b) => a.order - b.order)
                .map((ov) => (
                  <View style={{ width: '30%', marginRight: 50 }}>
                    <Text style={styles.bodyText}>
                      {t(`pt.results.label.${ov.id}`, { lng: lang })}{' '}
                      <Text style={{ ...styles.bodyText, fontSize: '14px' }}>
                        ({ov.employees} {t('pt.results.label.employees', { lng: lang })})
                      </Text>
                    </Text>
                    <View
                      style={{
                        backgroundColor: (ov.salary ?? 0) > salary ? '#FFD2D3' : '#F5F5F5',
                        paddingVertical: 8,
                        paddingHorizontal: 16,
                        marginTop: 8,
                        borderRadius: 4,
                      }}
                    >
                      <Text style={styles.bodyTextBold}>
                        {ov.salary !== null
                          ? `${ov.salary.toLocaleString(lang, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })} \u20ac`
                          : ''}
                      </Text>
                    </View>
                    {ov.salary !== null && ov.salary !== salary && (
                      <View style={{ display: 'flex', flexDirection: 'row', marginTop: 8 }}>
                        <Image
                          src={ov.salary > salary ? warning : info}
                          style={{
                            height: '16px',
                            width: '16px',
                            objectFit: 'contain',
                            marginRight: '8px',
                            alignItems: 'center',
                            marginTop: 2.5,
                          }}
                        />
                        <Text style={{ ...styles.bodyText, fontSize: '14px' }}>
                          {ov.salary > salary
                            ? t('pt.results.below', { lng: lang })
                            : t('pt.results.above', { lng: lang })}
                        </Text>
                      </View>
                    )}
                  </View>
                ))}
            </View>
          )}
          {tatImage && (
            <View style={{ width: '100%', height: 400, marginTop: '32px' }} wrap={false}>
              <Text style={styles.bodyTextBold}>{t('pt.results.graph.title', { lng: lang })}</Text>
              <Image src={tatImage} style={{ width: '100%', height: 350, objectFit: 'contain' }} />
            </View>
          )}
        </View>
        <View style={{ width: '50%', marginTop: 48 }} wrap={false}>
          <Text style={{ ...styles.bodyTextBold, fontSize: 20 }}>
            {t('pt.results.pdf.acc', { lng: lang })}
          </Text>
          <Text style={{ ...styles.bodyTextBold, marginTop: 16 }}>{`${t('pt.results.pdf.resp.per', {
            lng: lang,
          })}:`}</Text>
          <Text style={styles.bodyText}>{t('pt.results.pdf.resp.per.desc', { lng: lang })}</Text>
          <Text style={{ ...styles.bodyText, marginTop: 24 }}>
            {t('pt.results.pdf.resp.per', { lng: lang })}
          </Text>
          <View
            style={{
              border: '1px solid #DAF0FF',
              borderRadius: 2,
              width: '100%',
              height: 30,
              marginTop: 8,
              marginBottom: 24,
            }}
          />
          <Text style={styles.bodyText}>{t('pt.results.pdf.date.place', { lng: lang })}</Text>
          <View
            style={{
              border: '1px solid #DAF0FF',
              borderRadius: 2,
              width: '100%',
              height: 30,
              marginTop: 8,
              marginBottom: 24,
            }}
          />
          <Text style={styles.bodyText}>{t('pt.results.pdf.sign', { lng: lang })}</Text>
          <View
            style={{
              border: '1px solid #DAF0FF',
              borderRadius: 2,
              width: '100%',
              height: 90,
              marginTop: 8,
              marginBottom: 24,
            }}
          />
        </View>
        <View style={{ marginTop: 48 }} wrap={false}>
          <Text style={{ ...styles.bodyTextBold, fontSize: 20 }}>{t('pt.notes.title')}</Text>
          {[1, 2, 3, 4, 5, 6, 7].map((num) => (
            <View style={{ marginTop: 16, display: 'flex', flexDirection: 'row' }}>
              <View style={{ marginRight: 8 }}>
                <Text style={styles.bodySuperScript}>{num}</Text>
              </View>
              <View>
                <Text style={styles.bodyTextBold}>{t(`pt.notes.q.${num}`, { lng: lang })}</Text>
                <Text style={styles.bodyNotesText}>{t(`pt.notes.a.${num}`, { lng: lang })}</Text>
              </View>
            </View>
          ))}
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            position: 'absolute',
            right: 40,
            bottom: 40,
          }}
          fixed
        >
          <Text
            style={{ ...styles.bodyText, fontSize: 12 }}
            render={({ pageNumber, totalPages }) =>
              `${t('page', { lng: lang })} ${pageNumber} / ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  )

  const blob = await pdf(Template).toBlob()

  // For other browsers:
  // Create a link pointing to the ObjectURL containing the blob.
  const data = window.URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = data
  link.download = lang === 'de' ? 'Auskunft' : 'Information'

  // this is necessary as link.click() does not work on the latest firefox
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    }),
  )

  setTimeout(() => {
    // For Firefox it is necessary to delay revoking the ObjectURL
    window.URL.revokeObjectURL(data)
    link.remove()
  }, 100)

  return null
}
